import { Deferred, Head } from "@inertiajs/react"
import { Sparkles } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { ActivityErrorState } from "@/components/activity/activity-error-state"
import { EmptyState } from "@/components/empty-state"
import { ActivityFeedRow } from "@/components/notifications/activity-feed-row"
import { ActivityFilterChips } from "@/components/notifications/activity-filter-chips"
import { ActivitySectionCard } from "@/components/notifications/activity-section-card"
import { NotificationsLoadingSkeletons } from "@/components/notifications/notifications-loading-skeletons"
import { useActivityLive } from "@/hooks/use-activity-live"
import { useCurrentContext } from "@/hooks/use-current-context"
import AppLayout from "@/layouts/app-layout"
import { matchesChip } from "@/lib/activity-feed"
import { cn } from "@/lib/utils"
import type { ActivityChip, ActivityIndexProps } from "@/types/activity"
import type { NotificationRow } from "@/types/notifications"

const UNREAD_BADGE_OVERFLOW = 9

/**
 * OKS-548 — Creator Activity feed (`/activity`): the recognition surface.
 *
 * Evolves the shipped notifications feed (reuses its query, serializer,
 * `unread_notifications_count`, and `CreatorChannel` live signal) with a
 * recognition-oriented presentation: color-coded typed rows, Today/Earlier
 * section cards, client-side filter chips, an unread count, and a live
 * new-arrival pulse.
 *
 * OKS-552 — `activity_feed` is DEFERRED. The chrome (header + chips) paints
 * OUTSIDE the `<Deferred>` boundary so it is always present; the feed region
 * branches on the resolved tri-state (TRD §4 / KD-3):
 *   - `undefined` → not resolved → `<Deferred>` fallback = loading skeletons;
 *   - `null`      → resolved-but-failed (rescue) → inline error card;
 *   - `[]`        → resolved-empty → shipped empty state (DISTINCT from null);
 *   - `[...]`     → resolved feed.
 * The chip `selected` state is lifted here so the chips render identically in
 * the loading fallback and the resolved region.
 */
export default function CreatorActivityIndex({
  activity_feed,
  today_start_at,
}: ActivityIndexProps) {
  const { t } = useTranslation()
  const { unreadNotificationsCount } = useCurrentContext()
  const [selected, setSelected] = useState<ActivityChip>("all")

  // Filter chips are CHROME: they render OUTSIDE the `<Deferred>` boundary,
  // alongside the header, so they PERSIST across loading → error → feed. This
  // is the ticket's core acceptance ("header + chips remain" on a feed
  // failure) — rendering them inside `<Deferred>` made them vanish on the
  // null→error branch (which swaps in the error card). Loading (`undefined`)
  // and error (`null`) both keep the chips.
  //
  // The ONE exception: a resolved-EMPTY feed (`[]`). The shipped OKS-548 empty
  // state is a full takeover that deliberately hides the chips (UX Spec §3 —
  // nothing to filter, no count). That is resolved state the parent can see
  // directly, so we suppress the chips only then; every other state shows them.
  const isResolvedEmpty =
    Array.isArray(activity_feed) && activity_feed.length === 0

  return (
    <AppLayout>
      <Head title={t("activity.page.title")} />
      <main
        data-testid="activity-page"
        className="mx-auto flex w-full max-w-2xl flex-col gap-4 px-0 py-6 sm:px-6"
      >
        <ActivityHeader
          unreadCount={unreadNotificationsCount}
          showCount={unreadNotificationsCount > 0}
        />

        {!isResolvedEmpty && (
          <ActivityFilterChips selected={selected} onSelect={setSelected} />
        )}

        <Deferred
          data="activity_feed"
          fallback={
            <div className="px-4 sm:px-0">
              <NotificationsLoadingSkeletons />
            </div>
          }
        >
          <ActivityRegion
            rows={activity_feed}
            todayStartAt={today_start_at}
            selected={selected}
          />
        </Deferred>
      </main>
    </AppLayout>
  )
}

/**
 * Resolved feed region (rendered by `<Deferred>` once `activity_feed` is
 * present). The header + filter chips are chrome rendered by the parent
 * OUTSIDE this boundary; this component owns only the feed body — the
 * client-side filter (by the parent's `selected`) + Today/Earlier grouping +
 * live new-arrival detection — and branches the resolved tri-state:
 *   - `null` → error card (chrome above, incl. chips, stays put);
 *   - `[]`   → empty state (parent already suppressed the chips);
 *   - rows   → Today/Earlier sections / filtered-empty.
 */
function ActivityRegion({
  rows,
  todayStartAt,
  selected,
}: {
  rows: NotificationRow[] | null
  todayStartAt: string
  selected: ActivityChip
}) {
  const { t } = useTranslation()

  // Live new-arrival: partial-reloads `activity_feed` on a CreatorChannel
  // signal. The reload REPLACES `rows` with the fresh newest-first window.
  useActivityLive()

  // Session-relative "new-this-session" detection (KD-3): capture the row
  // ids present at mount ONCE (lazy `useState` initialiser → stable across
  // renders, safe to read during render). Any later id NOT in this set (and
  // landing in Today) is a live arrival → NEW pill + NOW + pulse. Id-diff,
  // not a wall-clock compare, so it's immune to client clock skew.
  const [seenIds] = useState<Set<number>>(
    () => new Set((rows ?? []).map((r) => r.id)),
  )

  // Resolved-but-failed (rescue → nil) → inline error card. Kept DISTINCT
  // from the resolved-empty `[]` branch below (UX Spec §2/§3): the header +
  // chips already painted above stay put; Retry reloads only `activity_feed`.
  if (rows === null) {
    return <ActivityErrorState />
  }

  // Whole feed empty → dedicated empty screen (no chips, no count).
  if (rows.length === 0) {
    return (
      <div className="px-4 sm:px-0">
        <EmptyState
          icon={Sparkles}
          title={t("activity.empty.title")}
          description={t("activity.empty.subtitle")}
          role="status"
          data-testid="activity-empty-state"
        />
      </div>
    )
  }

  const visible = rows.filter((r) => matchesChip(r.event_key, selected))
  const today = visible.filter((r) => r.created_at >= todayStartAt)
  const earlier = visible.filter((r) => r.created_at < todayStartAt)

  const isNew = (row: NotificationRow) =>
    !seenIds.has(row.id) && row.created_at >= todayStartAt

  return (
    <>
      {visible.length === 0 ? (
        // Filtered-empty (edge): chips stay, list replaced by a scoped
        // message. Cheers degrades here for a creator with no cheers.
        <div className="px-4 sm:px-0">
          <EmptyState
            icon={Sparkles}
            title={t("activity.filtered_empty.title", {
              filter: t(`activity.filters.${selected}`),
            })}
            role="status"
            data-testid="activity-filtered-empty"
          />
        </div>
      ) : (
        <div className="flex flex-col gap-4 px-4 sm:px-0">
          <ActivitySectionCard
            label={t("activity.groups.today")}
            testId="activity-section-today"
            isEmpty={today.length === 0}
          >
            {today.map((row) => (
              <ActivityFeedRow
                key={`${row.id}-${row.read_at ? "r" : "u"}`}
                notification={row}
                isNew={isNew(row)}
              />
            ))}
          </ActivitySectionCard>

          <ActivitySectionCard
            label={t("activity.groups.earlier")}
            testId="activity-section-earlier"
            isEmpty={earlier.length === 0}
          >
            {earlier.map((row) => (
              <ActivityFeedRow
                key={`${row.id}-${row.read_at ? "r" : "u"}`}
                notification={row}
              />
            ))}
          </ActivitySectionCard>
        </div>
      )}
    </>
  )
}

/**
 * Sticky feed header: title + unread count pill (UX Spec §2). The pill is
 * accent-filled, shows `9+` over 9, and is hidden at zero.
 */
function ActivityHeader({
  unreadCount,
  showCount,
}: {
  unreadCount: number
  showCount: boolean
}) {
  const { t } = useTranslation()
  return (
    <header
      data-testid="activity-header"
      className="bg-background/95 supports-[backdrop-filter]:bg-background/60 sticky top-14 z-10 flex items-center gap-3 px-4 pb-2 backdrop-blur sm:px-0"
    >
      <h1 className="text-xl font-semibold tracking-tight">
        {t("activity.page.title")}
      </h1>
      {showCount && (
        <span
          data-testid="activity-unread-count"
          data-count={unreadCount}
          className={cn(
            "bg-primary text-primary-foreground inline-flex h-5 min-w-5 items-center justify-center",
            "rounded-full px-1.5 text-xs font-semibold",
          )}
        >
          {unreadCount > UNREAD_BADGE_OVERFLOW
            ? t("activity.count_overflow")
            : unreadCount}
        </span>
      )}
    </header>
  )
}
